/**
 * Firestore Service (Frontend) - API Wrapper
 * ===========================================
 * This file is called from: Various frontend components (Dashboard, PageBuilder, FormBuilder, etc.)
 * 
 * IMPORTANT ARCHITECTURE CHANGE:
 * ------------------------------
 * This service NO LONGER connects directly to Firestore.
 * All database operations go through Backend API Server.
 * 
 * Data Flow:
 * Frontend Component → firestoreService → apiService → Backend API → Firestore (JAYRAM DB)
 * 
 * Why this approach:
 * 1. Security: No direct database credentials in frontend
 * 2. Consistency: Single source of truth (backend)
 * 3. Validation: Backend can validate all data
 * 4. Scalability: Can add caching, rate limiting at API layer
 * 
 * All functions maintain the same interface as before to avoid breaking existing components.
 */

import apiService from './apiService';

// ============================================
// APP OPERATIONS
// ============================================

/**
 * Fetch all applications from JAYRAM database via API
 * Called from: Dashboard component
 * 
 * Returns: Array of app objects with metadata
 * [
 *   {
 *     appName: "My App",
 *     appPrefix: "myapp",
 *     collections: [...],
 *     pageCount: 5,
 *     createdAt: "...",
 *     updatedAt: "..."
 *   },
 *   ...
 * ]
 */
export const fetchAllApps = async () => {
  try {
    console.log('🔍 [firestoreService] Fetching all apps via API...');
    const apps = await apiService.getAllApps();
    console.log(`✅ [firestoreService] Fetched ${apps.length} apps from JAYRAM database`);
    return apps || [];
  } catch (error) {
    console.error('❌ [firestoreService] Fetch apps failed:', error);
    return [];
  }
};

/**
 * fixed/updated by ChatGPT on 2025-10-19 11:02:31 – new: fetch only the current user’s apps by uid (Dashboard should prefer this)
 * Returns: same shape as fetchAllApps but filtered to the requesting user (createdBy or app_users mapping)
 */
export const fetchAppsByUser = async (uid) => {
  try {
    if (!uid) throw new Error('UID is required to fetch user apps');
    console.log('🔍 [firestoreService] Fetching user apps via API...', uid);
    const apps = await apiService.getAppsByUser(uid);
    console.log(`✅ [firestoreService] Fetched ${Array.isArray(apps) ? apps.length : (apps?.length || 0)} user apps from JAYRAM`);
    return apps || [];
  } catch (error) {
    console.error('❌ [firestoreService] Fetch user apps failed:', error);
    return [];
  }
};

/**
 * Delete an application and all its collections from JAYRAM database
 * Called from: Dashboard component
 * 
 * What it deletes:
 * - {appPrefix}_pages
 * - {appPrefix}_components
 * - {appPrefix}_layouts
 * - {appPrefix}_theme
 * - apps_meta entry
 */
export const deleteApp = async (appName) => {
  try {
    console.log(`🗑️ [firestoreService] Deleting app: ${appName}`);
    await apiService.deleteApp(appName);
    console.log(`✅ [firestoreService] App deleted from JAYRAM: ${appName}`);
    return true;
  } catch (error) {
    console.error('❌ [firestoreService] Delete app failed:', error);
    return false;
  }
};

// ============================================
// PAGE OPERATIONS
// ============================================

/**
 * Save multiple pages during app creation
 * Called from: AppCreationForm component (currently not used, backend creates empty collections)
 * 
 * Note: This is kept for backward compatibility but not actively used
 * since backend now creates empty collections during app creation
 */
export const savePages = async (appName, pages) => {
  try {
    if (!pages || pages.length === 0) {
      console.log(`⏭️ [firestoreService] No pages to save for ${appName}`);
      return true;
    }
    
    console.log(`💾 [firestoreService] Saving ${pages.length} pages for ${appName}...`);
    
    // Save each page via API
    for (const page of pages) {
      await apiService.savePage({
        appName,
        pageName: page.name || page.title || 'Untitled',
        pageData: page
      });
    }
    
    console.log(`✅ [firestoreService] Pages saved to JAYRAM database`);
    return true;
  } catch (error) {
    console.error('❌ [firestoreService] Save pages failed:', error);
    return false;
  }
};

/**
 * Update a single page in JAYRAM database
 * Called from: PageBuilder component
 */
export const updatePage = async (appName, pageName, pageData) => {
  try {
    console.log(`💾 [firestoreService] Updating page: ${appName}/${pageName}`);
    await apiService.savePage({
      appName,
      pageName,
      pageData
    });
    console.log(`✅ [firestoreService] Page updated in JAYRAM database`);
    return true;
  } catch (error) {
    console.error('❌ [firestoreService] Update page failed:', error);
    return false;
  }
};

/**
 * Save page version with version control logic
 * Called from: PageBuilder component
 * 
 * Handles versioning:
 * - Creates versions like "Home_v1", "Home_v2", "Home_v3"
 * - Max 3 versions per page
 * - Automatically disables old versions when new one is created
 */
export const savePageVersion = async (appName, baseName, pageData, isDefault = false) => {
  try {
    console.log(`💾 [firestoreService] Saving page version: ${appName}/${baseName}`);
    
    // Get existing versions to determine next version number
    const pages = await fetchAppPagesWithVersions(appName);
    const versions = pages.filter(p => p.baseName === baseName);
    const nextVersion = versions.length + 1;
    const versionedPageName = `${baseName}_v${nextVersion}`;
    
    // Check version limit (max 3 versions)
    if (versions.length >= 3) {
      // Find oldest version by date
      const oldestVersion = versions.sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt || 0);
        const dateB = new Date(b.updatedAt || b.createdAt || 0);
        return dateA - dateB;
      })[0];
      
      return {
        success: false,
        exceedsLimit: true,
        oldestVersion,
        newVersionData: {
          versionedPageName,
          version: nextVersion,
          pageDocName: versionedPageName
        },
        baseName
      };
    }
    
    // Disable all other versions if creating a new active version
    if (nextVersion > 1) {
      for (const v of versions) {
        await apiService.savePage({
          appName,
          pageName: v.id || v.name,
          pageData: { ...v, enabled: false }
        });
      }
    }
    
    // Save the new version with metadata
    const fullPageData = {
      ...pageData,
      baseName,                                  // Base page name (without version)
      version: nextVersion,                      // Version number (1, 2, 3)
      enabled: true,                             // This version is active
      isLatest: true,                            // This is the latest version
      isDefault: isDefault && nextVersion === 1, // Is this the default page for the app
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    await apiService.savePage({
      appName,
      pageName: versionedPageName,
      pageData: fullPageData
    });
    
    console.log(`✅ [firestoreService] Page version saved to JAYRAM: ${versionedPageName}`);
    
    return {
      success: true,
      versionedPageName,
      version: nextVersion,
      pageDocName: versionedPageName
    };
  } catch (error) {
    console.error('❌ [firestoreService] Save page version failed:', error);
    throw error;
  }
};

/**
 * Delete a page version from JAYRAM database
 * Called from: PageBuilder component
 */
export const deletePageVersion = async (appName, pageId) => {
  try {
    console.log(`🗑️ [firestoreService] Deleting page version: ${appName}/${pageId}`);
    await apiService.deletePage(appName, pageId);
    console.log(`✅ [firestoreService] Page version deleted from JAYRAM`);
    return true;
  } catch (error) {
    console.error('❌ [firestoreService] Delete page version failed:', error);
    return false;
  }
};

/**
 * Fetch all pages for an app with version metadata
 * Called from: PageSelector component
 * 
 * Returns: Array of pages with version info parsed from page names
 * [
 *   {
 *     id: "home_v1",
 *     name: "Home_v1",
 *     baseName: "Home",
 *     version: 1,
 *     enabled: true,
 *     ...pageData
 *   },
 *   ...
 * ]
 */
export const fetchAppPagesWithVersions = async (appName) => {
  try {
    console.log(`📄 [firestoreService] Fetching pages for app: ${appName}`);
    const pages = await apiService.getPagesByApp(appName);
    console.log(`✅ [firestoreService] Fetched ${pages.length} pages from JAYRAM`);
    
    // Parse version information from page names
    // e.g., "Home_v1" -> { baseName: "Home", version: 1 }
    return pages.map(page => {
      const match = page.name?.match(/^(.+)_v(\d+)$/);
      if (match) {
        return {
          ...page,
          baseName: match[1],
          version: parseInt(match[2], 10)
        };
      }
      return {
        ...page,
        baseName: page.name,
        version: 1
      };
    });
  } catch (error) {
    console.error('❌ [firestoreService] Fetch pages failed:', error);
    return [];
  }
};

/**
 * Fetch a specific page by name from JAYRAM database
 * Called from: PageSelector component
 */
export const fetchPageByName = async (appName, pageName) => {
  try {
    console.log(`📄 [firestoreService] Fetching page: ${appName}/${pageName}`);
    console.log(`📄 [firestoreService] API call: GET /pages/${appName}/${pageName}`);
    const page = await apiService.getPage(appName, pageName);
    console.log(`✅ [firestoreService] Page fetched from JAYRAM:`, page ? 'Success' : 'No data');
    return page;
  } catch (error) {
    console.error('❌ [firestoreService] Fetch page failed:', error);
    console.error('❌ [firestoreService] Error details:', error.message);
    return null;
  }
};

/**
 * Enable a disabled page version (activate old version)
 * Called from: PageSelector component
 * 
 * What it does:
 * 1. Disable the currently enabled page
 * 2. Enable the selected old version
 * 3. Mark it as the latest
 */
export const enablePageVersion = async (appName, pageId, currentEnabled) => {
  try {
    console.log(`✓ [firestoreService] Enabling page version: ${appName}/${pageId}`);
    
    // Fetch the page to enable
    const pageToEnable = await apiService.getPage(appName, pageId);
    if (!pageToEnable) {
      throw new Error('Page not found');
    }
    
    // Disable the currently enabled page
    if (currentEnabled) {
      await apiService.savePage({
        appName,
        pageName: currentEnabled.id || currentEnabled.name,
        pageData: { ...currentEnabled, enabled: false, isLatest: false }
      });
    }
    
    // Enable the target page
    await apiService.savePage({
      appName,
      pageName: pageId,
      pageData: { ...pageToEnable, enabled: true, isLatest: true }
    });
    
    console.log(`✅ [firestoreService] Page version enabled in JAYRAM`);
    return true;
  } catch (error) {
    console.error('❌ [firestoreService] Enable page version failed:', error);
    return false;
  }
};

// ============================================
// COMPONENT OPERATIONS
// ============================================

/**
 * Save multiple components during app creation
 * Called from: AppCreationForm component (currently not used)
 * 
 * Note: Kept for backward compatibility
 */
export const saveComponents = async (appName, components) => {
  try {
    if (!components || components.length === 0) {
      console.log(`⏭️ [firestoreService] No components to save for ${appName}`);
      return true;
    }
    
    console.log(`💾 [firestoreService] Saving ${components.length} components for ${appName}...`);
    
    // Save each component via API
    for (const component of components) {
      await apiService.saveComponent({
        appName,
        componentData: component
      });
    }
    
    console.log(`✅ [firestoreService] Components saved to JAYRAM database`);
    return true;
  } catch (error) {
    console.error('❌ [firestoreService] Save components failed:', error);
    return false;
  }
};

/**
 * Save component to library in JAYRAM database
 * Called from: FormBuilder component
 * Fixed by Claude on 2025-11-12 - Updated to accept appName parameter for proper collection naming
 */
export const saveComponentToLibrary = async (appName, componentData) => {
  try {
    console.log(`💾 [firestoreService] Saving component to library: ${componentData.name || componentData.type} for app: ${appName}`);
    await apiService.saveComponent({ appName, componentData });
    console.log(`✅ [firestoreService] Component saved to library in JAYRAM database`);
    return true; // Return boolean for backward compatibility
  } catch (error) {
    console.error('❌ [firestoreService] Save component to library failed:', error);
    return false; // Return boolean for backward compatibility
  }
};
// End of fix by Claude on 2025-11-12

/**
 * Fetch component library for an app from JAYRAM database
 * Called from: ComponentLibrarySelector component
 */
export const fetchComponentLibrary = async (appName) => {
  try {
    console.log(`📚 [firestoreService] Fetching component library for: ${appName}`);
    const components = await apiService.getAllComponents({ appName });
    console.log(`✅ [firestoreService] Fetched ${components.length} components from JAYRAM`);
    return components || [];
  } catch (error) {
    console.error('❌ [firestoreService] Fetch component library failed:', error);
    return [];
  }
};

/**
 * Generate unique component ID
 * This is a local utility function, no API call needed
 */
export const generateComponentId = () => {
  return `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// ============================================
// LAYOUT & THEME OPERATIONS
// ============================================

/**
 * Save layouts during app creation
 * Called from: AppCreationForm component (currently not used)
 * 
 * Note: Backend creates empty collection during app creation
 */
export const saveLayouts = async (appName, layouts) => {
  try {
    console.log(`💾 [firestoreService] Layouts for ${appName} (handled by backend)`);
    // Backend creates empty collection, no action needed
    return true;
  } catch (error) {
    console.error('❌ [firestoreService] Save layouts failed:', error);
    return false;
  }
};

/**
 * Save theme during app creation
 * Called from: AppCreationForm component (currently not used)
 * 
 * Note: Backend creates empty collection during app creation
 */
export const saveTheme = async (appName, theme) => {
  try {
    if (!theme || Object.keys(theme).length === 0) {
      console.log(`⏭️ [firestoreService] No theme to save for ${appName}`);
      return true;
    }
    
    console.log(`🎨 [firestoreService] Saving theme for ${appName}...`);
    await apiService.saveTheme({
      appName,
      themeData: {
        ...theme,
        savedAt: new Date().toISOString()
      }
    });
    console.log(`✅ [firestoreService] Theme saved to JAYRAM database`);
    return true;
  } catch (error) {
    console.error('❌ [firestoreService] Save theme failed:', error);
    return false;
  }
};

// ============================================
// UTILITY OPERATIONS (No API Calls)
// ============================================

/**
 * Save content during app creation
 * Local operation, no API call
 */
export const saveContent = async (appName, content) => {
  console.log(`📝 [firestoreService] Content for ${appName} (handled by backend)`);
  return true;
};

/**
 * Split design buckets - local data transformation
 * No API call, just processes JSON structure
 */
export const splitDesignBuckets = (generatedJSON) => {
  const content = generatedJSON?.content || {};
  const pages = generatedJSON?.pages || [];
  const components = generatedJSON?.components || [];
  const layouts = generatedJSON?.layouts || {};
  const theme = generatedJSON?.theme || {};
  
  return {
    content,
    pages,
    components,
    layouts,
    theme
  };
};

// ============================================
/** DEFAULT EXPORT **/
// ============================================

// export default {
//   // App operations
//   fetchAllApps,
//   deleteApp,
  
//   // Page operations
//   savePages,
//   updatePage,
//   savePageVersion,
//   deletePageVersion,
//   fetchAppPagesWithVersions,
//   fetchPageByName,
//   enablePageVersion,
  
//   // Component operations
//   saveComponents,
//   saveComponentToLibrary,
//   fetchComponentLibrary,
//   generateComponentId,
  
//   // Layout & Theme operations
//   saveLayouts,
//   saveTheme,
  
//   // Utility operations
//   saveContent,
//   splitDesignBuckets
// };
// fixed/updated by ChatGPT on 2025-10-19 11:02:31 – export now includes fetchAppsByUser while keeping all previous exports
export default {
  // App operations
  fetchAllApps,          // legacy (unfiltered)
  fetchAppsByUser,       // NEW: user-scoped list
  deleteApp,
  
  // Page operations
  savePages,
  updatePage,
  savePageVersion,
  deletePageVersion,
  fetchAppPagesWithVersions,
  fetchPageByName,
  enablePageVersion,
  
  // Component operations
  saveComponents,
  saveComponentToLibrary,
  fetchComponentLibrary,
  generateComponentId,
  
  // Layout & Theme operations
  saveLayouts,
  saveTheme,
  
  // Utility operations
  saveContent,
  splitDesignBuckets
};
